import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import * as Dialog from '@radix-ui/react-dialog';
import {
  TUpdateTicketData,
  SupportTicketCategory,
  SupportTicketStatus,
  SupportTicketPriority,
  TTicketData,
} from 'views/types/tickets';
import { ticketsService } from 'views/services/ticketsService';

type MetadataItem = { key: string; value: string };

type FormValues = Omit<TUpdateTicketData, 'metadata'> & {
  metadata?: MetadataItem[];
};

type Props = {
  open: boolean;
  setOpen: () => void;
  ticket: (TTicketData & { id: string }) | null;
};

export const EditTicketForm: React.FC<Props> = ({ open, setOpen, ticket }) => {
  const { register, handleSubmit, control, reset } = useForm<FormValues>({
    defaultValues: {
      metadata: [{ key: '', value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'metadata',
  });

  useEffect(() => {
    if (ticket) {
      reset({
        message: ticket.message,
        category: ticket.category,
        status: ticket.status,
        priority: ticket.priority,
        adminNotes: ticket.adminNotes,
        assignedAdminId: ticket.assignedAdminId,
        metadata: ticket.metadata
          ? Object.entries(ticket.metadata).map(([key, value]) => ({
              key,
              value: String(value),
            }))
          : [{ key: '', value: '' }],
      });
    }
  }, [ticket, reset]);

  const submitHandler = async (data: FormValues) => {
    const metadata = data.metadata?.reduce(
      (acc, { key, value }) => {
        if (key) acc[key] = value;
        return acc;
      },
      {} as Record<string, unknown>
    );

    const rawOutput: TUpdateTicketData = {
      ...data,
      metadata,
    };

    const output = Object.fromEntries(
      Object.entries(rawOutput).filter(
        ([_, value]) =>
          value !== '' &&
          value !== undefined &&
          value !== null &&
          (typeof value !== 'object' || Object.keys(value).length > 0)
      )
    );

    if (ticket) {
      await ticketsService.updateTicket(ticket.id, output);
      setOpen();
    }
  };

  if (!ticket) return null;

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 z-50 backdrop-blur-sm" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 h-[80vh] w-[90vw] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-navy-800 dark:text-white p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold text-center mb-4">
            Edit Ticket #{ticket.id}
          </Dialog.Title>

          <form onSubmit={handleSubmit(submitHandler)} className="space-y-4">
            <input
              {...register('message')}
              placeholder="Message"
              className="border p-2 w-full dark:bg-gray-700"
            />
            <input
              {...register('adminNotes')}
              placeholder="Admin Notes"
              className="border p-2 w-full dark:bg-gray-700"
            />
            <input
              {...register('assignedAdminId')}
              placeholder="Assigned Admin ID"
              className="border p-2 w-full dark:bg-gray-700"
            />

            <select
              {...register('category')}
              className="border p-2 w-full dark:bg-gray-700"
            >
              {Object.values(SupportTicketCategory).map(v => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>

            <select
              {...register('status')}
              className="border p-2 w-full dark:bg-gray-700"
            >
              {Object.values(SupportTicketStatus).map(v => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>

            <select
              {...register('priority')}
              className="border p-2 w-full dark:bg-gray-700"
            >
              {Object.values(SupportTicketPriority).map(v => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>

            <div>
              <label className="font-medium">Metadata</label>
              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-2 my-2">
                  <input
                    {...register(`metadata.${index}.key`)}
                    placeholder="Key"
                    className="border p-1 w-1/2 dark:bg-gray-700"
                  />
                  <input
                    {...register(`metadata.${index}.value`)}
                    placeholder="Value"
                    className="border p-1 w-1/2 dark:bg-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-red-500"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => append({ key: '', value: '' })}
                className="text-blue-500 mt-2"
              >
                + Add Metadata
              </button>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                className="bg-red-600 text-white px-4 py-2 rounded"
                onClick={setOpen}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
